/**
 * IncidentDetail.tsx — Expanded incident detail panel
 *
 * Purpose:    Shows incident timeline, acknowledge/resolve actions, and optional
 *             post-mortem URL input for a single selected incident.
 * Inputs:     incident: Incident, onAcknowledge, onResolve callbacks
 * Outputs:    Timeline list + action buttons; inline post-mortem URL field on resolve
 * Constraints: acknowledge only shown for 'open'; resolve shown for open+acknowledged
 */
import { useState } from 'react'
import type { Incident, IncidentSeverity } from '../../hooks/useIncidentData'

const SEVERITY_COLORS: Record<IncidentSeverity, string> = {
  p1: 'bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700/50',
  p2: 'bg-orange-50 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700/50',
  p3: 'bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700/50',
  p4: 'bg-brand-surface-2/50 text-brand-muted border-brand-card/50',
}

const STATUS_LABEL: Record<Incident['status'], { label: string; className: string }> = {
  open: { label: 'Open', className: 'text-red-700 dark:text-red-400' },
  acknowledged: { label: 'Acknowledged', className: 'text-yellow-700 dark:text-yellow-400' },
  resolved: { label: 'Resolved', className: 'text-green-700 dark:text-green-400' },
}

export { SEVERITY_COLORS, STATUS_LABEL }

export function SeverityBadge({ severity }: { severity: IncidentSeverity }) {
  return (
    <span className={`rounded border px-1.5 py-0.5 text-xs font-bold uppercase ${SEVERITY_COLORS[severity]}`}>
      {severity.toUpperCase()}
    </span>
  )
}

export function IncidentDetail({
  incident,
  onAcknowledge,
  onResolve,
}: {
  incident: Incident
  onAcknowledge: (id: string) => Promise<boolean>
  onResolve: (id: string, postMortemUrl?: string) => Promise<boolean>
}) {
  const [actioning, setActioning] = useState(false)
  const [postMortemUrl, setPostMortemUrl] = useState(incident.postMortemUrl ?? '')

  const handleAcknowledge = async () => {
    setActioning(true)
    await onAcknowledge(incident.id)
    setActioning(false)
  }

  const handleResolve = async () => {
    setActioning(true)
    await onResolve(incident.id, postMortemUrl || undefined)
    setActioning(false)
  }

  return (
    <div className="rounded-lg border border-brand-card bg-brand-surface p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SeverityBadge severity={incident.severity} />
        <span className={`text-sm font-medium ${STATUS_LABEL[incident.status].className}`}>
          {STATUS_LABEL[incident.status].label}
        </span>
        <h3 className="text-base font-semibold text-brand-ink">{incident.title}</h3>
      </div>

      <ol className="mb-4 space-y-2 border-l border-brand-card pl-4">
        {incident.timeline.map((entry) => (
          <li key={entry.id} className="text-sm">
            <time className="text-xs text-brand-muted">{new Date(entry.createdAt).toLocaleString()}</time>
            <span className="ml-2 text-brand-secondary">
              <strong className="text-gray-200">{entry.author}</strong> — {entry.message}
            </span>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap gap-2">
        {incident.status === 'open' && (
          <button
            type="button"
            disabled={actioning}
            onClick={() => { void handleAcknowledge() }}
            className="rounded-md bg-yellow-700 px-3 py-1.5 text-sm font-medium text-brand-ink hover:bg-yellow-600 disabled:opacity-50"
          >
            Acknowledge
          </button>
        )}
        {incident.status !== 'resolved' && (
          <div className="flex items-center gap-2">
            <input
              type="url"
              value={postMortemUrl}
              onChange={(e) => setPostMortemUrl(e.target.value)}
              placeholder="Post-mortem URL (optional)"
              className="rounded border border-brand-card bg-brand-surface-2 px-2 py-1 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none"
            />
            <button
              type="button"
              disabled={actioning}
              onClick={() => { void handleResolve() }}
              className="rounded-md bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50"
            >
              Resolve
            </button>
          </div>
        )}
        {incident.runbookUrl && (
          <a
            href={incident.runbookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2"
          >
            View runbook
          </a>
        )}
      </div>
    </div>
  )
}
